import { useEffect, useState } from "react";
import { api } from "../lib/api";

type Event = {
  id: number;
  created_at: string;
  event_type: string;
  customer_name: string | null;
  customer_id: number | null;
  delta: number;
  note: string | null;
};

type Decision = {
  id: number;
  created_at: string;
  function_name: string;
  inbound_text: string | null;
  decision: string | null;
  route_reason: string | null;
  outcome: string | null;
};

function eventEmoji(t: string) {
  switch (t) {
    case "punch_added": return "👊";
    case "reward_unlocked": return "🎁";
    case "reward_redeemed": return "✅";
    case "customer_enrolled": return "🆕";
    default: return "•";
  }
}

export function Activity() {
  const [tab, setTab] = useState<"events" | "decisions">("events");
  const [events, setEvents] = useState<Event[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setErr(null);
    if (tab === "events") {
      api<{ data: Event[] }>("/api/activity?kind=events").then((r) => setEvents(r.data)).catch((e: Error) => setErr(e.message));
    } else {
      api<{ data: Decision[] }>("/api/activity?kind=decisions").then((r) => setDecisions(r.data)).catch((e: Error) => setErr(e.message));
    }
  }, [tab]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-xl font-semibold">Activity</h1>
        <div className="flex gap-2 text-sm">
          <button onClick={() => setTab("events")} className={`px-2 py-1 rounded ${tab === "events" ? "bg-punchy-500 text-white" : "bg-neutral-200"}`}>Events</button>
          <button onClick={() => setTab("decisions")} className={`px-2 py-1 rounded ${tab === "decisions" ? "bg-punchy-500 text-white" : "bg-neutral-200"}`}>Decisions</button>
        </div>
      </div>
      {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
      <ul className="bg-white border border-neutral-200 rounded-lg divide-y divide-neutral-100">
        {tab === "events" && events.length === 0 && <li className="px-4 py-6 text-center text-neutral-500">No events yet.</li>}
        {tab === "events" && events.map((e) => (
          <li key={e.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
            <span className="text-lg">{eventEmoji(e.event_type)}</span>
            <div className="flex-1 min-w-0">
              <div>
                <span className="font-medium">{e.customer_name ?? `customer ${e.customer_id ?? "?"}`}</span>
                <span className="text-neutral-500"> · {e.event_type}{e.delta ? ` (${e.delta > 0 ? "+" : ""}${e.delta})` : ""}</span>
              </div>
              {e.note && <div className="text-xs text-neutral-500 truncate">{e.note}</div>}
            </div>
            <div className="text-xs text-neutral-400 whitespace-nowrap">{e.created_at}</div>
          </li>
        ))}
        {tab === "decisions" && decisions.length === 0 && <li className="px-4 py-6 text-center text-neutral-500">No decisions yet.</li>}
        {tab === "decisions" && decisions.map((d) => (
          <li key={d.id} className="px-4 py-2.5 text-sm">
            <div className="flex items-center gap-2">
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                d.outcome === "ok" ? "bg-green-100 text-green-800" :
                d.outcome === "rejected" ? "bg-amber-100 text-amber-800" :
                d.outcome === "error" ? "bg-red-100 text-red-800" : "bg-neutral-100"
              }`}>{d.outcome}</span>
              <span className="font-medium">{d.decision ?? "?"}</span>
              <span className="text-neutral-500">· {d.route_reason}</span>
              <span className="ml-auto text-xs text-neutral-400">{d.created_at}</span>
            </div>
            {d.inbound_text && <div className="text-xs text-neutral-500 truncate">in: "{d.inbound_text}"</div>}
          </li>
        ))}
      </ul>
    </div>
  );
}
